import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { pushRecent } from '@/hooks/useRecentJobs';
import {
  ArrowLeft,
  Download,
  ExternalLink,
  FileText,
  RefreshCw,
  SkipForward,
  Sparkles,
  Loader2,
  CheckCircle2,
  Calendar,
  XCircle,
  Trophy,
  Pencil,
  Mail,
  CheckSquare,
  Square,
  ClipboardCheck,
  Tag as TagIcon,
  X,
  Bell,
  BellOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/services/api';
import { useFetch } from '@/hooks/useFetch';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { ResumeDiff as ResumeDiffView } from '@/components/ResumeDiff';
import { formatDateTime, companyDomain } from '@/lib/format';
import type { JobDetail, ResumeDiff, JobStatus, Job } from '@/services/types';

function mailtoForRecruiter(j: Job): string {
  const company = j.company ?? 'your team';
  const title = j.title ?? 'the role';
  const subject = `Following up on my application — ${title} at ${company}`;
  const body = [
    'Hi,',
    '',
    `I recently applied for the ${title} role at ${company} and wanted to check in.`,
    "I'm very interested in the opportunity and would welcome the chance to discuss how my background fits the team.",
    '',
    'Happy to share more on a quick call if helpful.',
    '',
    'Best,',
    'Peter Chen',
  ].join('\n');
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const DEFAULT_CHECKLIST = [
  'Re-read the JD top to bottom',
  'List 3 questions about team/product/metrics',
  'Prepare a 90-sec story matching their top requirement',
  'Look up the hiring manager + 1st-round interviewer on LinkedIn',
  'Skim the company blog/last quarter changelog',
];

interface PrepItem { text: string; done: boolean }

function parseChecklist(notes: { id: number; body: string }[]): { id: number | null; items: PrepItem[] } {
  for (const n of notes) {
    if (n.body.startsWith('PREP_CHECKLIST::')) {
      try {
        const items = JSON.parse(n.body.slice('PREP_CHECKLIST::'.length)) as PrepItem[];
        if (Array.isArray(items)) return { id: n.id, items };
      } catch {
        /* fall through to default */
      }
    }
  }
  return { id: null, items: DEFAULT_CHECKLIST.map((text) => ({ text, done: false })) };
}

function parseTags(s: string | null | undefined): string[] {
  if (!s) return [];
  try {
    const arr = JSON.parse(s);
    return Array.isArray(arr) ? arr.filter((t) => typeof t === 'string') : [];
  } catch {
    return [];
  }
}

function TagsCard({ job, onChanged }: { job: Job; onChanged: () => void }) {
  const [tags, setTags] = useState<string[]>(parseTags(job.tags));
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTags(parseTags(job.tags));
  }, [job.tags]);

  async function commit(next: string[]) {
    setBusy(true);
    const prev = tags;
    setTags(next);
    try {
      await api.post(`/jobs/${job.id}/tags`, { tags: next });
      onChanged();
    } catch (e) {
      setTags(prev);
      toast.error(e instanceof ApiError ? e.message : 'tags_failed');
    } finally {
      setBusy(false);
    }
  }

  function add(raw: string) {
    const t = raw.trim().toLowerCase();
    if (!t || tags.includes(t) || tags.length >= 16) return;
    commit([...tags, t]);
    setInput('');
  }

  function remove(t: string) {
    commit(tags.filter((x) => x !== t));
  }

  return (
    <Card title="Tags">
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-500/10 dark:text-primary-300"
          >
            <TagIcon className="h-3 w-3" />
            {t}
            <button
              onClick={() => remove(t)}
              disabled={busy}
              className="text-primary-700/60 hover:text-primary-700 dark:text-primary-300/60"
              title="Remove"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {tags.length < 16 && (
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                add(input);
              }
            }}
            placeholder={tags.length === 0 ? 'add a tag (Enter to commit)…' : '+ tag'}
            className="input text-xs"
          />
        )}
      </div>
    </Card>
  );
}

function toLocalDatetimeInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ReminderCard({ job, onChanged }: { job: Job; onChanged: () => void }) {
  const [dt, setDt] = useState(toLocalDatetimeInput(job.remind_at));
  const [msg, setMsg] = useState(job.remind_message ?? '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDt(toLocalDatetimeInput(job.remind_at));
    setMsg(job.remind_message ?? '');
  }, [job.remind_at, job.remind_message]);

  const isDue =
    job.remind_at && !job.remind_acknowledged_at && new Date(job.remind_at) <= new Date();
  const isFuture = job.remind_at && !job.remind_acknowledged_at && !isDue;

  async function save() {
    if (!dt) {
      toast.error('Pick a date/time first');
      return;
    }
    setBusy(true);
    try {
      const iso = new Date(dt).toISOString();
      await api.post(`/jobs/${job.id}/reminder`, {
        remind_at: iso,
        message: msg.trim() || null,
      });
      toast.success('Reminder set');
      onChanged();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'reminder_failed');
    } finally {
      setBusy(false);
    }
  }

  async function ack() {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/reminder/ack`);
      toast.success('Marked done');
      onChanged();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'ack_failed');
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    try {
      await api.delete(`/jobs/${job.id}/reminder`);
      toast.success('Reminder cleared');
      setDt('');
      setMsg('');
      onChanged();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'clear_failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Follow-up reminder"
      action={
        isDue ? (
          <span className="badge-amber flex items-center gap-1">
            <Bell className="h-3 w-3" />
            Due
          </span>
        ) : isFuture ? (
          <span className="badge-blue flex items-center gap-1">
            <Bell className="h-3 w-3" />
            Scheduled
          </span>
        ) : job.remind_acknowledged_at ? (
          <span className="badge-green flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Done
          </span>
        ) : null
      }
    >
      <div className="space-y-2">
        <input
          type="datetime-local"
          value={dt}
          onChange={(e) => setDt(e.target.value)}
          className="input text-sm"
        />
        <input
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          placeholder="Message (e.g. ping recruiter on LinkedIn)"
          className="input text-sm"
        />
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className="btn-primary text-xs">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
            {job.remind_at ? 'Update' : 'Set reminder'}
          </button>
          {job.remind_at && !job.remind_acknowledged_at && (
            <button onClick={ack} disabled={busy} className="btn-secondary text-xs">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Mark done
            </button>
          )}
          {job.remind_at && (
            <button onClick={clear} disabled={busy} className="btn-secondary text-xs">
              <BellOff className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

function PrepChecklist({
  jobId,
  initialNotes,
  onChanged,
}: {
  jobId: number;
  initialNotes: { id: number; body: string }[];
  onChanged: () => void;
}) {
  const initial = parseChecklist(initialNotes);
  const [items, setItems] = useState<PrepItem[]>(initial.items);
  const [saving, setSaving] = useState(false);

  async function save(next: PrepItem[]) {
    setSaving(true);
    try {
      await api.post(`/jobs/${jobId}/notes`, {
        body: `PREP_CHECKLIST::${JSON.stringify(next)}`,
        applicant_id: 'peter',
      });
      onChanged();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'save_failed');
    } finally {
      setSaving(false);
    }
  }

  function toggle(idx: number) {
    const next = items.map((it, i) => (i === idx ? { ...it, done: !it.done } : it));
    setItems(next);
    save(next);
  }

  return (
    <Card title={<><ClipboardCheck className="mr-1 inline h-4 w-4 text-primary-600" />Interview prep</>}>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i}>
            <button
              onClick={() => toggle(i)}
              disabled={saving}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              {it.done ? (
                <CheckSquare className="h-4 w-4 shrink-0 text-green-600" />
              ) : (
                <Square className="h-4 w-4 shrink-0 text-gray-400" />
              )}
              <span className={it.done ? 'text-gray-400 line-through' : 'text-gray-700 dark:text-gray-200'}>
                {it.text}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

interface JdInsights {
  keywords: string[] | null;
  jd_excerpt: string | null;
  source_files: string[];
}

export default function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, refresh } = useFetch<JobDetail>(id ? `/jobs/${id}` : null);
  const diffQ = useFetch<ResumeDiff>(id && data?.resume.has_docx ? `/jobs/${id}/resume-diff` : null);
  const jdQ = useFetch<JdInsights>(id && data?.resume.dir ? `/jobs/${id}/jd-insights` : null);
  const [noteBody, setNoteBody] = useState('');
  const [cover, setCover] = useState<{ markdown: string; cached?: boolean } | null>(null);
  const [coverLoading, setCoverLoading] = useState(false);
  const [coverTemplates, setCoverTemplates] = useState<
    Array<{ id: number; name: string; tone: string | null; body: string }>
  >([]);

  useEffect(() => {
    if (!data?.job?.applicant_id) return;
    api
      .get<{ items: typeof coverTemplates }>(
        `/cover-letter-templates?applicant_id=${data.job.applicant_id}`,
      )
      .then((r) => setCoverTemplates(r.items))
      .catch(() => setCoverTemplates([]));
  }, [data?.job?.applicant_id]);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editCompany, setEditCompany] = useState('');
  const [editLocation, setEditLocation] = useState('');

  useEffect(() => {
    if (data?.job?.id) {
      pushRecent({ id: data.job.id, company: data.job.company, title: data.job.title });
    }
  }, [data?.job?.id, data?.job?.company, data?.job?.title]);

  if (loading && !data) {
    return (
      <div className="grid place-items-center py-20 text-gray-400">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return <div className="card p-4 text-sm text-red-700">Failed to load: {error ?? 'unknown'}</div>;
  }

  const j = data.job;
  const logo = companyDomain(j.company);

  async function retry() {
    try {
      await api.post(`/jobs/${id}/retry`);
      toast.success('Queued for retry');
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'retry_failed');
    }
  }
  async function skip() {
    try {
      await api.post(`/jobs/${id}/skip`, { reason: 'manual skip from admin' });
      toast.success('Skipped');
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'skip_failed');
    }
  }
  async function addNote(e: FormEvent) {
    e.preventDefault();
    if (!noteBody.trim()) return;
    try {
      await api.post(`/jobs/${id}/notes`, { body: noteBody.trim(), applicant_id: j.applicant_id ?? 'peter' });
      setNoteBody('');
      toast.success('Note added');
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'note_failed');
    }
  }
  async function setStatus(status: JobStatus) {
    try {
      await api.post(`/jobs/${id}/status`, { status, note: `set via admin UI` });
      toast.success(`Marked as ${status}`);
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'status_failed');
    }
  }
  function startEdit() {
    setEditTitle(j.title ?? '');
    setEditCompany(j.company ?? '');
    setEditLocation(j.location ?? '');
    setEditing(true);
  }
  async function saveEdit() {
    try {
      await api.patch(`/jobs/${id}`, {
        title: editTitle,
        company: editCompany,
        location: editLocation,
      });
      toast.success('Saved');
      setEditing(false);
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'save_failed');
    }
  }

  async function generateCover() {
    setCoverLoading(true);
    try {
      const r = await api.post<{ markdown: string; cached: boolean }>(`/jobs/${id}/cover-letter`, {});
      setCover(r);
      toast.success(r.cached ? 'Loaded cached cover letter' : 'Cover letter generated');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'cover_failed');
    } finally {
      setCoverLoading(false);
    }
  }

  return (
    <div className="space-y-5">
      <Link to="/applications" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-primary-700">
        <ArrowLeft className="h-4 w-4" />
        Applications
      </Link>

      <header className="flex items-start gap-4">
        {logo && (
          <img
            src={`https://logo.clearbit.com/${logo}`}
            alt=""
            className="h-12 w-12 shrink-0 rounded-md border border-gray-200 bg-white object-contain"
            onError={(e) => ((e.currentTarget as HTMLImageElement).style.visibility = 'hidden')}
          />
        )}
        <div className="flex-1">
          {editing ? (
            <div className="space-y-2">
              <input
                value={editCompany}
                onChange={(e) => setEditCompany(e.target.value)}
                className="input"
                placeholder="Company"
              />
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="input"
                placeholder="Title"
              />
              <input
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                className="input"
                placeholder="Location"
              />
              <div className="flex gap-2">
                <button onClick={saveEdit} className="btn-primary text-xs">
                  Save
                </button>
                <button onClick={() => setEditing(false)} className="btn-secondary text-xs">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold tracking-tight text-gray-900">{j.company}</h2>
                <button
                  onClick={startEdit}
                  title="Edit"
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-sm text-gray-600">{j.title}</p>
              <p className="text-xs text-gray-500">
                {j.location} · {j.source} · #{j.id}
              </p>
            </>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={j.status} />
          <span className="text-xs text-gray-500">{formatDateTime(j.applied_at ?? j.discovered_at)}</span>
        </div>
      </header>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setStatus('applied' as JobStatus)} className="btn-secondary">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          Mark applied
        </button>
        <button onClick={() => setStatus('interview' as JobStatus)} className="btn-secondary">
          <Calendar className="h-4 w-4 text-primary-600" />
          Mark interview
        </button>
        <button onClick={() => setStatus('offer' as JobStatus)} className="btn-secondary">
          <Trophy className="h-4 w-4 text-amber-500" />
          Mark offer
        </button>
        <button onClick={() => setStatus('rejected' as JobStatus)} className="btn-secondary">
          <XCircle className="h-4 w-4 text-red-600" />
          Mark rejected
        </button>
        {(j.status === 'failed' || j.status === 'skipped') && (
          <button onClick={retry} className="btn-primary">
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        )}
        {j.status !== 'skipped' && j.status !== 'applied' && (
          <button onClick={skip} className="btn-secondary">
            <SkipForward className="h-4 w-4" />
            Skip
          </button>
        )}
        {j.url && (
          <a href={j.url} target="_blank" rel="noreferrer" className="btn-secondary">
            <ExternalLink className="h-4 w-4" />
            Open posting
          </a>
        )}
        {data.resume.has_docx && (
          <a href={`/api/jobs/${id}/resume.docx`} className="btn-secondary">
            <Download className="h-4 w-4" />
            DOCX
          </a>
        )}
        {(data.resume.has_pdf || data.resume.has_docx) && (
          <a href={`/api/jobs/${id}/resume.pdf`} target="_blank" rel="noreferrer" className="btn-secondary">
            <FileText className="h-4 w-4" />
            Open PDF
          </a>
        )}
        <a
          href={mailtoForRecruiter(j)}
          className="btn-secondary"
          title="Compose a recruiter follow-up email"
        >
          <Mail className="h-4 w-4" />
          Email recruiter
        </a>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {(data.resume.has_docx || data.resume.has_pdf) && (
            <Card title="Resume preview">
              <iframe
                src={`/api/jobs/${id}/resume.pdf#zoom=page-width`}
                title="Resume preview"
                className="h-[640px] w-full rounded-md border border-gray-200"
              />
            </Card>
          )}

          {data.resume.has_docx && (
            <Card
              title="Diff vs master"
              action={
                <span className="text-xs text-gray-500">
                  {diffQ.loading
                    ? 'computing…'
                    : diffQ.data
                      ? `${diffQ.data.customized_para_count} paragraphs`
                      : ''}
                </span>
              }
            >
              {diffQ.error && <p className="text-sm text-red-700">{diffQ.error}</p>}
              {diffQ.data && <ResumeDiffView diff={diffQ.data} />}
              {!diffQ.data && diffQ.loading && (
                <div className="flex items-center justify-center py-8 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              )}
            </Card>
          )}

          {jdQ.data?.keywords && jdQ.data.keywords.length > 0 && (
            <Card title="JD keywords">
              <div className="flex flex-wrap gap-1.5">
                {jdQ.data.keywords.slice(0, 60).map((k, i) => (
                  <span key={i} className="badge-blue text-xs">
                    {k}
                  </span>
                ))}
              </div>
            </Card>
          )}

          <Card title="Job description">
            {j.description ? (
              <pre className="whitespace-pre-wrap text-xs leading-relaxed text-gray-700">
                {j.description.slice(0, 6000)}
              </pre>
            ) : (
              <p className="text-sm text-gray-400">No JD captured.</p>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card
            title="Cover letter"
            action={
              <div className="flex items-center gap-2">
                {coverTemplates.length > 0 && (
                  <select
                    className="input text-xs"
                    defaultValue=""
                    onChange={(e) => {
                      const t = coverTemplates.find((x) => String(x.id) === e.target.value);
                      if (!t) return;
                      const filled = t.body
                        .replace(/\{\{\s*company\s*\}\}/gi, j.company)
                        .replace(/\{\{\s*role\s*\}\}/gi, j.title);
                      setCover({ markdown: filled, cached: false });
                      e.target.value = '';
                    }}
                  >
                    <option value="" disabled>
                      Insert template…
                    </option>
                    {coverTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                        {t.tone ? ` (${t.tone})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                <button onClick={generateCover} disabled={coverLoading} className="btn-primary text-xs">
                  {coverLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {cover ? 'Regenerate' : 'Generate'}
                </button>
              </div>
            }
          >
            {cover ? (
              <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">
                {cover.markdown}
              </pre>
            ) : (
              <p className="text-sm text-gray-500">
                Click Generate to draft with Claude, or insert a saved template from your library.
              </p>
            )}
          </Card>

          <Card title="Status timeline">
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between gap-2 text-gray-700">
                <span>discovered</span>
                <span className="text-xs text-gray-500">{formatDateTime(j.discovered_at)}</span>
              </li>
              {j.applied_at && (
                <li className="flex justify-between gap-2 text-gray-700">
                  <span>applied via {j.applied_via ?? j.source}</span>
                  <span className="text-xs text-gray-500">{formatDateTime(j.applied_at)}</span>
                </li>
              )}
              {data.log.map((l) => (
                <li key={l.id} className="flex justify-between gap-2 text-gray-700">
                  <span>
                    {l.action} {l.details ? <span className="text-gray-400">— {l.details.slice(0, 60)}</span> : null}
                  </span>
                  <span className="text-xs text-gray-500">{formatDateTime(l.timestamp)}</span>
                </li>
              ))}
              {data.signals.map((s) => (
                <li key={`s-${s.id}`} className="flex justify-between gap-2 text-red-700">
                  <span>📩 {s.kind} — {s.raw_subject?.slice(0, 60)}</span>
                  <span className="text-xs text-gray-500">{formatDateTime(s.created_at)}</span>
                </li>
              ))}
            </ul>
          </Card>

          <TagsCard job={j} onChanged={refresh} />

          <ReminderCard job={j} onChanged={refresh} />

          <PrepChecklist jobId={Number(id)} initialNotes={data.notes} onChanged={refresh} />

          <Card title="Notes">
            <form onSubmit={addNote} className="space-y-2">
              <textarea
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                placeholder="e.g. recruiter responded on LinkedIn, follow up Friday"
                className="input min-h-[80px]"
              />
              <button type="submit" className="btn-secondary">
                Add note
              </button>
            </form>
            <ul className="mt-3 space-y-2">
              {data.notes.map((n) => (
                <li key={n.id} className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  {n.body}
                  <div className="mt-0.5 text-xs text-gray-400">{formatDateTime(n.created_at)}</div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
